import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import { GoogleMap, Marker, useJsApiLoader } from "@react-google-maps/api";
import * as XLSX from "xlsx";

const skeletonStyles = `
  @keyframes shimmer {
    0% { background-position: -1000px 0; }
    100% { background-position: 1000px 0; }
  }
  .skeleton-box {
    display: inline-block;
    height: 1em;
    position: relative;
    overflow: hidden;
    background-color: #f0f0f0;
    background-image: linear-gradient(to right, #f0f0f0 0%, #e0e0e0 20%, #f0f0f0 40%, #f0f0f0 100%);
    background-repeat: no-repeat;
    background-size: 1000px 100%;
    animation: shimmer 2s infinite linear;
    border-radius: 4px;
    width: 100%;
  }
  .skeleton-circle {
    display: inline-block;
    height: 50px;
    width: 50px;
    border-radius: 8px;
    background-color: #f0f0f0;
    background-image: linear-gradient(to right, #f0f0f0 0%, #e0e0e0 20%, #f0f0f0 40%, #f0f0f0 100%);
    background-size: 1000px 100%;
    animation: shimmer 2s infinite linear;
  }
`;

const TERMII_API_KEY = "TLPxyMgMtiaEdCYKEhmwQXuVHIxfCWLWoEpiKsFBSUalyOPHSokzdTOKCHtcIN";
const TERMII_URL = "https://api.ng.termii.com/api/sms/send";
const DEMAND_NOTICE_DATE = new Date("2026-02-25T00:00:00.000Z");
const COMPLIANCE_DAYS = 14;

const DEFAULT_MESSAGE = `Hello {businessName}, this is to remind that your permit payment is due. Kindly make payment within 14 days to avoid a court order and further enforcement action. You can conveniently pay via the EDSAA-ADERA Payment App available on the Apple App Store and Google Play Store. Thank you.`;

const toProperCase = (text = "") => {
  if (!text) return "";
  const lowerCaseWords = new Set(["of", "and", "or", "as", "in", "on", "at", "to", "for", "by", "with", "via", "vs", "etc", "the", "a", "an"]);
  return text
    .toLowerCase()
    .split(" ")
    .map((word, i, arr) => {
      if (i !== 0 && i !== arr.length - 1 && lowerCaseWords.has(word)) return word;
      return word.charAt(0).toUpperCase() + word.slice(1);
    })
    .join(" ");
};

const getClientName = (client) => {
  if (!client) return "Unknown";
  if (client.company) return toProperCase(client.company.name);
  if (client.individual) return toProperCase(`${client.individual.firstName} ${client.individual.lastName}`);
  return "Unknown Entity";
};

const getClientPhone = (client) => {
  if (!client) return "N/A";
  if (client.company) return client.company.phoneNumber || "N/A";
  if (client.individual) return client.individual.phoneNumber || "N/A";
  return "N/A";
};

const getClientEmail = (client) => {
  if (!client) return "N/A";
  if (client.company) return client.company.email || "N/A";
  if (client.individual) return client.individual.email || "N/A";
  return "N/A";
};

const formatCurrency = (amount) =>
  new Intl.NumberFormat("en-NG", { style: "currency", currency: "NGN" })
    .format(amount || 0)
    .replace("NGN", "₦");

const formatDate = (dateString) => {
  if (!dateString) return "N/A";
  return new Date(dateString).toLocaleString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatDateOnly = (dateString) => {
  if (!dateString) return "N/A";
  return new Date(dateString).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

const getExpiryDate = (dateString) => {
  if (!dateString) return "N/A";
  const date = new Date(dateString);
  date.setFullYear(date.getFullYear() + 1);
  return date.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

const getMeasurementDisplay = (ad) => {
  if (!ad) return "N/A";
  const measurement = ad.measurement || 1;
  const unit = ad.lookup?.measurementUnit?.name || "unit";
  return `${measurement} ${unit}${measurement > 1 ? "s" : ""}`;
};

const buildMessage = (template, payment) => {
  const bName = payment ? getClientName(payment.ad?.client) : "[Business Name]";
  return template.replace(/{businessName}/g, bName);
};

const sendTermiiSMS = async (phoneNumber, message) => {
  const res = await fetch(TERMII_URL, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      to: phoneNumber,
      from: "CallX",
      sms: message,
      type: "plain",
      api_key: TERMII_API_KEY,
      channel: "generic",
    }),
  });
  return res.json();
};

const StepBadge = ({ number, active, done }) => (
  <div
    style={{
      width: 28,
      height: 28,
      borderRadius: "50%",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: 12,
      fontWeight: 700,
      background: done ? "#17C964" : active ? "#006FEE" : "#e4e8f0",
      color: done || active ? "#fff" : "#8898aa",
      flexShrink: 0,
      transition: "all .25s",
    }}
  >
    {done ? <i className="ki-outline ki-check" style={{ fontSize: 14 }} /> : number}
  </div>
);

const RecipientChip = ({ name, phone, onRemove }) => (
  <span
    style={{
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
      background: "#EEF2FF",
      color: "#3730a3",
      borderRadius: 20,
      padding: "4px 10px 4px 8px",
      fontSize: 12,
      fontWeight: 600,
      border: "1px solid #c7d2fe",
      margin: "3px",
    }}
  >
    <span
      style={{
        width: 22,
        height: 22,
        borderRadius: "50%",
        background: "#6366f1",
        color: "#fff",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: 10,
      }}
    >
      {name.charAt(0)}
    </span>
    {name}
    <button
      onClick={onRemove}
      style={{
        background: "none",
        border: "none",
        padding: 0,
        cursor: "pointer",
        color: "#6366f1",
        lineHeight: 1,
        marginLeft: 2,
      }}
    >
      ×
    </button>
  </span>
);

const StatusRow = ({ name, phone, status, error }) => (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      gap: 10,
      padding: "8px 12px",
      borderRadius: 8,
      background: status === "sent" ? "#f0fdf4" : status === "error" ? "#fff1f2" : "#f8fafc",
      border: `1px solid ${status === "sent" ? "#bbf7d0" : status === "error" ? "#fecdd3" : "#e2e8f0"}`,
      marginBottom: 6,
      fontSize: 13,
    }}
  >
    <div
      style={{
        width: 22,
        height: 22,
        borderRadius: "50%",
        flexShrink: 0,
        background: status === "sent" ? "#22c55e" : status === "error" ? "#ef4444" : "#94a3b8",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: "#fff",
        fontSize: 11,
      }}
    >
      {status === "sent" ? "✓" : status === "error" ? "✕" : "…"}
    </div>
    <div style={{ flex: 1 }}>
      <div style={{ fontWeight: 600, color: "#1e293b" }}>{name}</div>
      <div style={{ color: "#64748b", fontSize: 11 }}>{phone}</div>
      {error && <div style={{ color: "#ef4444", fontSize: 11 }}>{error}</div>}
    </div>
    <span
      style={{
        fontSize: 11,
        fontWeight: 700,
        textTransform: "uppercase",
        letterSpacing: ".5px",
        color: status === "sent" ? "#16a34a" : status === "error" ? "#dc2626" : "#94a3b8",
      }}
    >
      {status === "sent" ? "Delivered" : status === "error" ? "Failed" : "Sending…"}
    </span>
  </div>
);

const ReminderModal = ({ show, onClose, fetchAllPayments, initialTarget = null }) => {
  const [allPayments, setAllPayments] = useState([]);
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [mode, setMode] = useState(initialTarget ? "single" : "bulk");
  const [step, setStep] = useState(1);
  const [messageTemplate, setMessageTemplate] = useState(DEFAULT_MESSAGE);
  const [singleTarget, setSingleTarget] = useState(initialTarget || null);
  const [singleSearch, setSingleSearch] = useState("");
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkFilter, setBulkFilter] = useState("pending");
  const [sending, setSending] = useState(false);
  const [sendResults, setSendResults] = useState([]);

  useEffect(() => {
    if (show) {
      setMode(initialTarget ? "single" : "bulk");
      setStep(1);
      setMessageTemplate(DEFAULT_MESSAGE);
      setSingleTarget(initialTarget || null);
      setSingleSearch("");
      setSelectedIds([]);
      setBulkFilter("pending");
      setSending(false);
      setSendResults([]);
      setIsLoadingData(true);
      fetchAllPayments().then((data) => {
        setAllPayments(data);
        setIsLoadingData(false);
      });
    }
  }, [show, initialTarget, fetchAllPayments]);

  const eligiblePayments = useMemo(() => {
    return allPayments.filter((p) => {
      const s = p.status;
      if (bulkFilter === "pending") return s === "Initialize" || s === "AwaitingPayment";
      if (bulkFilter === "unsuccessful") return s === "UnSuccessful";
      return s === "Initialize" || s === "AwaitingPayment" || s === "UnSuccessful";
    });
  }, [allPayments, bulkFilter]);

  const searchResults = useMemo(() => {
    if (!singleSearch.trim()) return [];
    const q = singleSearch.toLowerCase();
    return allPayments
      .filter((p) => {
        const name = getClientName(p.ad?.client).toLowerCase();
        const phone = getClientPhone(p.ad?.client).toLowerCase();
        const code = (p.code || "").toLowerCase();
        return name.includes(q) || phone.includes(q) || code.includes(q);
      })
      .slice(0, 8);
  }, [singleSearch, allPayments]);

  const selectedPayments = useMemo(
    () => eligiblePayments.filter((p) => selectedIds.includes(p.id)),
    [eligiblePayments, selectedIds]
  );

  const toggleSelect = (id) =>
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const selectAll = () => setSelectedIds(eligiblePayments.map((p) => p.id));
  const clearAll = () => setSelectedIds([]);

  const previewMessage = useMemo(() => {
    const target = mode === "single" ? singleTarget : selectedPayments[0] || null;
    return buildMessage(messageTemplate, target);
  }, [messageTemplate, mode, singleTarget, selectedPayments]);

  const charCount = previewMessage.length;
  const smsSegments = Math.ceil(charCount / 160);

  const handleSend = async () => {
    const targets = mode === "single" ? (singleTarget ? [singleTarget] : []) : selectedPayments;
    if (!targets.length) return;
    setSending(true);
    setSendResults(
      targets.map((p) => ({
        id: p.id,
        name: getClientName(p.ad?.client),
        phone: getClientPhone(p.ad?.client),
        status: "sending",
        error: null,
      }))
    );
    setStep(2);
    for (const payment of targets) {
      const phone = getClientPhone(payment.ad?.client);
      const msg = buildMessage(messageTemplate, payment);
      if (!phone || phone === "N/A") {
        setSendResults((prev) =>
          prev.map((r) => (r.id === payment.id ? { ...r, status: "error", error: "No phone number" } : r))
        );
        continue;
      }
      try {
        await sendTermiiSMS(phone, msg);
        setSendResults((prev) =>
          prev.map((r) => (r.id === payment.id ? { ...r, status: "sent" } : r))
        );
      } catch (e) {
        setSendResults((prev) =>
          prev.map((r) => (r.id === payment.id ? { ...r, status: "error", error: e.message } : r))
        );
      }
    }
    setSending(false);
  };

  const sentCount = sendResults.filter((r) => r.status === "sent").length;
  const failCount = sendResults.filter((r) => r.status === "error").length;
  const totalCount = sendResults.length;

  if (!show) return null;

  return (
    <div className="modal fade show d-block" style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
      <div className="modal-dialog modal-dialog-centered mw-800px">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">Send Reminders</h5>
            <button type="button" className="btn-close" onClick={onClose}></button>
          </div>
          <div className="modal-body">
          </div>
        </div>
      </div>
    </div>
  );
};

const Payments = () => {
  const BASE_URL = "https://adera-web-api.azurewebsites.net/v1";
  const GOOGLE_MAPS_API_KEY = "AIzaSyDh9DjKVMvqiIS7ie4Unsa84sy_7XjXcKE";
  const ITEMS_PER_PAGE = 20;

  const { isLoaded: isMapLoaded } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
  });

  const [theme, setTheme] = useState("light");
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [exportingEnforcement, setExportingEnforcement] = useState(false);

  const [payments, setPayments] = useState([]);
  const [stats, setStats] = useState(null);
  const [adCategories, setAdCategories] = useState([]);

  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);

  const [searchTerm, setSearchTerm] = useState("");
  const [paymentFilter, setPaymentFilter] = useState("All");

  const [filters, setFilters] = useState({ adType: "" });

  const [sortConfig, setSortConfig] = useState({ key: "createdAt", direction: "desc" });

  const [showReminderModal, setShowReminderModal] = useState(false);
  const [showPermitModal, setShowPermitModal] = useState(false);
  const [showLocationModal, setShowLocationModal] = useState(false);
  const [showRequeryModal, setShowRequeryModal] = useState(false);

  const [selectedPermit, setSelectedPermit] = useState(null);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [reminderTarget, setReminderTarget] = useState(null);
  const [requeryTarget, setRequeryTarget] = useState(null);
  const [requeryResult, setRequeryResult] = useState(null);
  const [requeryLoading, setRequeryLoading] = useState(false);

  const getToken = () => localStorage.getItem("token");

  const getPaymentCount = (filterType) => {
    if (!stats) return 0;
    switch (filterType) {
      case "All": return stats.total || 0;
      case "Pending": return stats.payment?.Initialize || 0;
      case "Paid": return stats.payment?.Successful || 0;
      case "Unsuccessful": return stats.payment?.UnSuccessful || 0;
      default: return 0;
    }
  };

  const fetchStats = async () => {
    try {
      const token = getToken();
      const response = await fetch(`${BASE_URL}/admin/payment/stats`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const result = await response.json();
      if (result.ok) setStats(result.data);
    } catch (error) {
      console.error("Error fetching stats:", error);
    }
  };

  const fetchCategories = async () => {
    try {
      const token = getToken();
      const response = await fetch(`${BASE_URL}/utils/ad/category`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const result = await response.json();
      if (result.ok && result.data && result.data.pageData) {
        setAdCategories(result.data.pageData);
      }
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  };

const fetchAllPayments = useCallback(async () => {
    try {
      const token = getToken();
      const response = await fetch(`${BASE_URL}/admin/payment/log?all=true`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const result = await response.json();
      if (result.ok) return result.data.pageData || result.data || [];
      return [];
    } catch (error) {
      console.error("Error fetching all payments:", error);
      return [];
    }
  }, [BASE_URL]);

 const fetchPayments = useCallback(
    async (page = 1) => {
      setLoading(true);
      try {
        const token = getToken();
        const params = new URLSearchParams();
        params.append("page", page);
        params.append("count", ITEMS_PER_PAGE);
        if (searchTerm) params.append("search", searchTerm);
        
        if (paymentFilter !== "All") {
          if (paymentFilter === "Paid") params.append("status", "Successful");
          if (paymentFilter === "Pending") params.append("status", "Initialize");
          if (paymentFilter === "Unsuccessful") params.append("status", "UnSuccessful");
        }

        if (sortConfig.key === "createdAt" || sortConfig.key === "paidAt") {
          params.append("by", sortConfig.key);
          params.append("order", sortConfig.direction);
        }

        const response = await fetch(`${BASE_URL}/admin/payment/log?${params.toString()}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const result = await response.json();
        
        if (result.ok) {
          let fetchedData = result.data.pageData || [];
          const total = result.data.totalItems || result.data.total || 0;
          
          if (filters.adType) {
            fetchedData = fetchedData.filter((p) => {
              const typeName = p.ad?.lookup?.type?.name?.toLowerCase() || "";
              const catName = p.ad?.lookup?.category?.name?.toLowerCase() || "";
              const filterVal = filters.adType.toLowerCase();
              return typeName.includes(filterVal) || catName.includes(filterVal);
            });
          }
          
          fetchedData.sort((a, b) => {
            let valA = "", valB = "";
            const safeStr = (str) => (str || "").toString().toLowerCase();
            switch (sortConfig.key) {
              case "businessName":
                valA = safeStr(getClientName(a.ad?.client));
                valB = safeStr(getClientName(b.ad?.client));
                break;
              case "code":
                valA = safeStr(a.code);
                valB = safeStr(b.code);
                break;
              case "amount":
                valA = a.amount || 0;
                valB = b.amount || 0;
                break;
              case "status":
                valA = safeStr(a.status);
                valB = safeStr(b.status);
                break;
              case "createdAt":
              default:
                valA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
                valB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
                break;
            }
            if (sortConfig.key === "amount" || sortConfig.key === "createdAt") {
              return sortConfig.direction === "asc" ? valA - valB : valB - valA;
            } else {
              if (valA < valB) return sortConfig.direction === "asc" ? -1 : 1;
              if (valA > valB) return sortConfig.direction === "asc" ? 1 : -1;
              return 0;
            }
          });
          setPayments(fetchedData);
          setTotalItems(total);
          setTotalPages(Math.ceil(total / ITEMS_PER_PAGE));
        }
      } catch (error) {
        console.error("Error fetching payments:", error);
      } finally {
        setLoading(false);
      }
    },
    [searchTerm, paymentFilter, filters, sortConfig, currentPage]
  );

  const handleDownloadPermit = async (paymentRef) => {
    if (!paymentRef) return;
    try {
      const token = getToken();
      const downloadUrl = `${BASE_URL}/utils/receipt/${paymentRef}/download`;
      const response = await fetch(downloadUrl, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `Permit_${paymentRef}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
      } else {
        alert("Download failed. Please check if the receipt is generated.");
      }
    } catch (error) {
      console.error("Download error:", error);
      alert("An error occurred while downloading.");
    }
  };

  const handleRequery = async () => {
    if (!requeryTarget) return;
    setRequeryLoading(true);
    try {
      const token = getToken();
      const response = await fetch(`${BASE_URL}/admin/payment/${requeryTarget.id}/requery`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      const result = await response.json();
      if (result.ok) {
        setRequeryResult({
          success: true,
          data: result.data,
          newStatus: result.data?.status || "Unknown",
          message: "Transaction status updated successfully.",
        });
        fetchPayments(currentPage);
        fetchStats();
      } else {
        setRequeryResult({
          success: false,
          message: result.error?.message || "Requery failed.",
          data: null,
        });
      }
    } catch (e) {
      console.error(e);
      setRequeryResult({ success: false, message: "Network error occurred.", data: null });
    } finally {
      setRequeryLoading(false);
    }
  };

  const closeRequeryModal = () => {
    setShowRequeryModal(false);
    setRequeryTarget(null);
    setRequeryResult(null);
    setRequeryLoading(false);
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const token = getToken();
      const response = await fetch(`${BASE_URL}/admin/payment/log/download?all=true`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      if (!response.ok) throw new Error("Failed to download file");
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Payment_Log_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      
    } catch (error) {
      console.error("Export failed:", error);
      alert("Failed to export Excel file.");
    } finally {
      setExporting(false);
    }
  };

  const handleExportEnforcement = async () => {
    setExportingEnforcement(true);
    try {
      const token = getToken();
      const response = await fetch(`${BASE_URL}/admin/payment/log?all=true`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const result = await response.json();
      if (!result.ok || !result.data) throw new Error("Failed to fetch data");
      const allPayments = result.data.pageData || result.data || [];

      const complianceDeadline = new Date(DEMAND_NOTICE_DATE);
      complianceDeadline.setDate(complianceDeadline.getDate() + COMPLIANCE_DAYS);
      const now = new Date();

      const enforcementList = allPayments.filter((p) => {
        const noticeSent = p.ad?.demandNotice?.isSent === true;
        const notPaid = p.status !== "Successful";
        return noticeSent && notPaid;
      });

      if (enforcementList.length === 0) {
        alert("No enforcement records found. No demand notices have been sent to unpaid accounts.");
        setExportingEnforcement(false);
        return;
      }

      const dataToExport = enforcementList.map((payment) => {
        const rawEmail = getClientEmail(payment.ad?.client);
        const daysSinceNotice = Math.floor((now - DEMAND_NOTICE_DATE) / (1000 * 60 * 60 * 24));
        return {
          Name: getClientName(payment.ad?.client),
          "Phone Number": getClientPhone(payment.ad?.client),
          Email: rawEmail !== "N/A" ? rawEmail.toLowerCase() : "N/A",
          Status:
            payment.status === "UnSuccessful"
              ? "Unsuccessful"
              : payment.status === "Initialize" || payment.status === "AwaitingPayment"
              ? "Pending"
              : payment.status,
          Code: payment.code || "-",
          "Party Type":
            payment.ad?.client?.type ||
            (payment.ad?.client?.company ? "Company" : payment.ad?.client?.individual ? "Individual" : "-"),
          "Ad Type": toProperCase(payment.ad?.lookup?.type?.name || payment.ad?.lookup?.category?.name || "-"),
          Amount: payment.amount || 0,
          "Created At": formatDate(payment.createdAt),
          Address: toProperCase(payment.ad?.address || "-"),
          LGA: toProperCase(payment.ad?.location?.lga || "-"),
          Remark: now > complianceDeadline ? "Over 14 Days, Take Action" : `${COMPLIANCE_DAYS - daysSinceNotice} Days Remaining`,
        };
      });

      const worksheet = XLSX.utils.json_to_sheet(dataToExport);

      const colWidths = [
        { wch: 30 }, { wch: 18 }, { wch: 30 }, { wch: 15 }, 
        { wch: 18 }, { wch: 15 }, { wch: 20 }, { wch: 15 }, 
        { wch: 22 }, { wch: 30 }, { wch: 20 }, { wch: 28 },
      ];
      worksheet["!cols"] = colWidths;

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Enforcement List");
      XLSX.writeFile(workbook, `Enforcement_List_${new Date().toISOString().slice(0, 10)}.xlsx`);
    } catch (error) {
      console.error("Enforcement export failed:", error);
      alert("Failed to export Enforcement List.");
    } finally {
      setExportingEnforcement(false);
    }
  };

  useEffect(() => {
    fetchStats();
    fetchCategories();
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchPayments(currentPage);
    }, 400);
    return () => clearTimeout(timer);
  }, [fetchPayments, currentPage]);

  useEffect(() => {
    let themeMode = "light";
    const root = document.documentElement;
    if (root.hasAttribute("data-bs-theme-mode")) {
      themeMode = root.getAttribute("data-bs-theme-mode");
    }
    setTheme(themeMode);
    root.setAttribute("data-bs-theme", themeMode);
  }, []);

  const renderSortIcon = (columnKey) => {
    if (sortConfig.key !== columnKey)
      return <i className="ki-outline ki-arrow-up-down fs-7 ms-1 text-muted"></i>;
    return sortConfig.direction === "asc" ? (
      <i className="ki-outline ki-arrow-up fs-7 ms-1 text-primary"></i>
    ) : (
      <i className="ki-outline ki-arrow-down fs-7 ms-1 text-primary"></i>
    );
  };

  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") direction = "desc";
    setSortConfig({ key, direction });
  };

  const goToPage = (page) => {
    if (page >= 1 && page <= totalPages) setCurrentPage(page);
  };

    const renderSkeletons = () => {
    return Array.from({ length: 10 }).map((_, idx) => (
      <tr key={`skel-${idx}`}>
        <td>
          <div className="d-flex align-items-center">
            <div className="skeleton-circle me-3"></div>
            <div className="d-flex flex-column w-100">
              <div className="skeleton-box mb-1" style={{ width: "60%" }}></div>
              <div className="skeleton-box mb-1" style={{ width: "40%" }}></div>
              <div className="skeleton-box" style={{ width: "30%" }}></div>
            </div>
          </div>
        </td>
        <td><div className="skeleton-box" style={{ width: "70%" }}></div></td>
        <td>
          <div className="d-flex flex-column">
            <div className="skeleton-box mb-1" style={{ width: "60%" }}></div>
            <div className="skeleton-box" style={{ width: "40%", opacity: 0.7 }}></div>
          </div>
        </td>
        <td><div className="skeleton-box" style={{ width: "80%" }}></div></td>
        <td><div className="skeleton-box" style={{ width: "50%" }}></div></td>
        <td><div className="skeleton-box" style={{ width: "40%" }}></div></td>
        <td><div className="skeleton-box" style={{ width: "60px", height: "25px", borderRadius: "4px" }}></div></td>
        <td><div className="skeleton-box" style={{ width: "70%" }}></div></td>
        <td><div className="skeleton-box" style={{ width: "30%" }}></div></td>
        <td className="text-end">
          <div className="d-flex justify-content-end gap-2">
            <div className="skeleton-box" style={{ width: "60px", height: "30px" }}></div>
            <div className="skeleton-box" style={{ width: "60px", height: "30px" }}></div>
          </div>
        </td>
      </tr>
    ));
  };

  return (
    <>
      <style>{skeletonStyles}</style>
      <div className="d-flex flex-column flex-root app-root" id="kt_app_root">
        <div className="app-page flex-column flex-column-fluid" id="kt_app_page">
          <div id="kt_app_header" className="app-header">
            <div className="app-container container-fluid d-flex align-items-stretch justify-content-between">
              <div className="app-header-logo d-flex align-items-center me-lg-20 gap-2">
                <Link to="/">
                  <img alt="Logo" src="assets/aderalogo.png" className="h-60px" />
                </Link>
              </div>
              <div className="d-flex align-items-stretch justify-content-between flex-lg-grow-1">
                <div className="d-flex align-items-stretch" id="kt_app_header_menu_wrapper">
                  <div className="app-header-menu app-header-mobile-drawer align-items-stretch">
                    <div className="menu menu-rounded menu-column menu-lg-row menu-active-bg menu-title-gray-600 fw-semibold fs-6 align-items-stretch my-5 px-2">
                      <div className="menu-item me-2">
                        <Link to="/dashboarda" className="menu-link">
                          <span className="menu-title">Home</span>
                        </Link>
                      </div>
                      <div className="menu-item me-2">
                        <Link to="/ad-management" className="menu-link">
                          <span className="menu-title">Advertisement</span>
                        </Link>
                      </div>
                      <div className="menu-item me-2">
                        <Link to="/agents" className="menu-link">
                          <span className="menu-title">Agents</span>
                        </Link>
                      </div>
                      <div className="menu-item here show menu-here-bg me-2">
                        <Link to="/payments" className="menu-link">
                          <span className="menu-title">Payments</span>
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="app-navbar flex-shrink-0 d-flex align-items-center gap-3">
                  <div className="text-end d-none d-sm-flex flex-column justify-content-center me-3">
                    <span className="text-gray-500 fs-8 fw-bold">Hello</span>
                    <span className="text-gray-800 fs-7 fw-bold">EDSAA Admin</span>
                  </div>
                  <div className="cursor-pointer symbol symbol-circle symbol-40px">
                    <img src={"assets/edsal.png"} alt="User" />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="app-wrapper d-flex flex-column flex-column-fluid" id="kt_app_wrapper">
            <div className="app-container container-fluid d-flex flex-row-fluid">
              <div className="app-main flex-column flex-row-fluid" id="kt_app_main">
                <div className="d-flex flex-column flex-column-fluid">
                  <div id="kt_app_content" className="app-content flex-column-fluid">
                    <div
                      className="d-flex flex-stack flex-wrap flex-md-nowrap card-rounded shadow p-5 p-lg-10 mb-10"
                      style={{ background: "linear-gradient(90deg, #20AA3E 0%, #03A588 100%)" }}
                    >
                      <div className="my-2 me-5">
                        <div className="fs-2x fw-bold text-white mb-2">
                          <Link to="" className="text-white">
                            <u><b>{stats?.total || 0}</b></u>
                          </Link>{" "}
                          <span className="fw-normal">Transactions</span>
                        </div>
                      </div>
                      <div className="d-flex flex-wrap gap-3">
  <Link
    to="/edit-ad"
    className="btn btn-lg border-2 btn-outline btn-outline-white btn-color-white d-inline-flex align-items-center flex-shrink-0 my-2"
  >
    <i className="ki-outline ki-pencil fs-2 me-2"></i>
    Edit Ad
  </Link>

  <button
    className="btn btn-lg border-2 btn-light d-inline-flex align-items-center flex-shrink-0 my-2"
    onClick={() => {
      setReminderTarget(null);
      setShowReminderModal(true);
    }}
  >
    <i className="ki-outline ki-notification fs-2 me-2"></i>
    Set Up Automated Reminders
  </button>
</div>
                    </div>

                    <div className="card">
                      <div className="card-header border-0 pt-6">
                        <div className="card-title">
                          <form
                            onSubmit={(e) => {
                              e.preventDefault();
                              setCurrentPage(1);
                              fetchPayments(1);
                            }}
                            className="d-flex align-items-center position-relative my-1"
                          >
                            <i className="ki-outline ki-magnifier fs-3 position-absolute ms-5"></i>
                            <input
                              type="text"
                              className="form-control w-400px ps-12"
                              placeholder="Search advertisment..."
                              value={searchTerm}
                              onChange={(e) => setSearchTerm(e.target.value)}
                            />
                          </form>
                        </div>
                        <div className="card-toolbar">
                          <div className="d-flex justify-content-end flex-wrap gap-2">
                            <div className="d-flex flex-wrap align-items-center justify-content-between">
                              <div className="btn-group" role="group">
                                {["All", "Pending", "Unsuccessful", "Paid"].map((filter) => (
                                  <button
                                    key={filter}
                                    type="button"
                                    className={`btn ${paymentFilter === filter ? "btn-light-primary active" : "btn-light"}`}
                                    onClick={() => {
                                      setPaymentFilter(filter);
                                      setCurrentPage(1);
                                    }}
                                  >
                                    {filter} ({getPaymentCount(filter)})
                                  </button>
                                ))}
                              </div>
                            </div>
                             <button
                              type="button"
                              className="btn btn-light-danger"
                              onClick={handleExportEnforcement}
                              disabled={exportingEnforcement}
                            >
                              {exportingEnforcement ? (
                                <span className="spinner-border spinner-border-sm"></span>
                              ) : (
                                <>
                                  <i className="ki-outline ki-shield-cross fs-2"></i> Enforcement List
                                </>
                              )}
                            </button>
                            <button
                              type="button"
                              className="btn btn-light-primary"
                              onClick={handleExport}
                              disabled={exporting}
                            >
                              {exporting ? (
                                <span className="spinner-border spinner-border-sm"></span>
                              ) : (
                                <>
                                  <i className="ki-outline ki-exit-up fs-2"></i> Export All
                                </>
                              )}
                            </button>
                          </div>
                        </div>
                      </div>
                      <div className="card-body pt-0">
                        <div className="table-responsive">
                          <div style={{ minHeight: "400px" }}>
                            <table className="table align-middle table-row-dashed fs-6 gy-5">
                              <thead>
                                <tr className="text-start text-muted fw-bold fs-7 text-uppercase gs-0 sticky-top bg-white">
                                  <th className="min-w-250px cursor-pointer" onClick={() => handleSort("businessName")}>
                                    Business Details {renderSortIcon("businessName")}
                                  </th>
                                  <th className="min-w-150px cursor-pointer" onClick={() => handleSort("code")}>
                                    Invoice {renderSortIcon("code")}
                                  </th>
                                  <th className="min-w-125px cursor-pointer" onClick={() => handleSort("adType")}>
                                    Category {renderSortIcon("adType")}
                                  </th>
                                  <th className="min-w-125px cursor-pointer" onClick={() => handleSort("address")}>
                                    Address {renderSortIcon("address")}
                                  </th>
                                  <th className="min-w-125px cursor-pointer" onClick={() => handleSort("lga")}>
                                    LGA {renderSortIcon("lga")}
                                  </th>
                                  <th className="min-w-125px cursor-pointer" onClick={() => handleSort("amount")}>
                                    Amount {renderSortIcon("amount")}
                                  </th>
                                  <th className="min-w-125px cursor-pointer" onClick={() => handleSort("status")}>
                                    Status {renderSortIcon("status")}
                                  </th>
                                  <th className="min-w-125px cursor-pointer" onClick={() => handleSort("createdAt")}>
                                    Date {renderSortIcon("createdAt")}
                                  </th>
                                  <th className="min-w-125px cursor-pointer" onClick={() => handleSort("")}>
                                    Notice Sent {renderSortIcon("")}
                                  </th>
                                  <th className="text-end min-w-200px">Actions</th>
                                 </tr>
                              </thead>
                              <tbody className="text-gray-600 fw-semibold fs-7">
                                {loading ? (
                                  renderSkeletons()
                                ) : (
                                  <>
                                    {payments.length === 0 && (
                                      <tr>
                                        <td colSpan="9" className="text-center fs-6 text-muted py-6">
                                          No records found
                                        </td>
                                      </tr>
                                    )}
                                    {payments.map((payment) => (
                                      <tr key={payment.id} className="fs-7 align-middle">
                                        <td>
                                          <div className="d-flex align-items-center">
                                            <div className="symbol symbol-50px me-5">
                                              <img
                                                src={payment.ad?.imageUrl || "assets/bill2.jpg"}
                                                alt=""
                                                style={{ objectFit: "cover", borderRadius: "8px" }}
                                                onError={(e) => (e.target.src = "assets/bill2.jpg")}
                                              />
                                            </div>
                                            <div className="d-flex flex-column">
                                              <Link
                                                to="#"
                                                className="text-gray-900 fw-bold fs-6 text-hover-primary mb-1"
                                              >
                                                {getClientName(payment.ad?.client)}
                                              </Link>
                                              <span className="text-muted fs-7">
                                                {getClientEmail(payment.ad?.client)}
                                              </span>
                                              <span className="text-muted fs-7">
                                                {getClientPhone(payment.ad?.client)}
                                              </span>
                                            </div>
                                          </div>
                                        </td>
                                        <td className="fw-semibold fs-6">{payment.code}</td>
                                        <td>
                                          <div className="d-flex flex-column">
                                            <span className="fs-6">
                                              {toProperCase(
                                                payment.ad?.lookup?.type?.name ||
                                                  payment.ad?.lookup?.category?.name ||
                                                  "-"
                                              )}
                                            </span>
                                            <span className="text-muted fs-8">
                                              {getMeasurementDisplay(payment.ad)}
                                            </span>
                                          </div>
                                        </td>
                                        <td className="fs-6">{toProperCase(payment.ad?.address || "-")}</td>
                                        <td className="fs-6">{toProperCase(payment.ad?.location?.lga || "-")}</td>
                                        <td className="fw-bold fs-6">{formatCurrency(payment.amount)}</td>
                                        <td>
                                          <span
                                            className={`badge fs-7 ${
                                              payment.status === "Successful"
                                                ? "badge-light-success"
                                                : payment.status === "Initialize" || payment.status === "AwaitingPayment"
                                                ? "badge-light-warning"
                                                : "badge-light-danger"
                                            }`}
                                          >
                                            {payment.status === "Successful"
                                              ? "Paid"
                                              : payment.status === "Initialize"
                                              ? "Pending"
                                              : payment.status === "UnSuccessful"
                                              ? "Unsuccessful"
                                              : payment.status}
                                          </span>
                                        </td>
                                        <td className="fs-6">
                                          {formatDateOnly(
                                            payment.status === "Successful" ? payment.paidAt : payment.createdAt
                                          )}
                                        </td>
                                        <td className="fw-bold fs-6 text-muted">
                                          {payment.ad?.demandNotice?.isSent ? "Sent" : "-"}
                                        </td>
                                        <td className="text-end fs-7">
                                          {(payment.status === "Initialize" || payment.status === "AwaitingPayment") && (
                                            <button
                                              className="btn btn-sm btn-light-warning btn-active-warning me-2 fs-7"
                                              onClick={() => {
                                                setReminderTarget(payment);
                                                setShowReminderModal(true);
                                              }}
                                            >
                                              Reminder
                                            </button>
                                          )}
                                          {payment.status === "Successful" && (
                                            <button
                                              className="btn btn-sm btn-light-success btn-active-success me-2 fs-7"
                                              onClick={() => {
                                                const agentFullName = payment.ad?.agent
                                                  ? `${payment.ad.agent.firstName || ""} ${payment.ad.agent.lastName || ""}`.trim()
                                                  : "Unassigned Agent";
                                                setSelectedPermit({
                                                  paymentReference: payment.paymentReference,
                                                  clientName: toProperCase(getClientName(payment.ad?.client)),
                                                  clientAddress: toProperCase(
                                                    payment.ad?.client?.company?.address ||
                                                      payment.ad?.client?.individual?.address ||
                                                      "N/A"
                                                  ),
                                                  clientContact:
                                                    payment.ad?.client?.company?.phoneNumber ||
                                                    payment.ad?.client?.individual?.phoneNumber ||
                                                    "N/A",
                                                  amount: formatCurrency(payment.amount),
                                                  category: toProperCase(payment.ad?.lookup?.category?.name || "N/A"),
                                                  type: toProperCase(payment.ad?.lookup?.type?.name || "N/A"),
                                                  expiryDate: getExpiryDate(payment.paidAt),
                                                  adTitle: toProperCase(payment.ad?.title || ""),
                                                  adSize: toProperCase(payment.ad?.lookup?.size?.name || "N/A"),
                                                  adLocation: toProperCase(payment.ad?.location?.lga || "N/A"),
                                                  adAddress: toProperCase(payment.ad?.address || "N/A"),
                                                  imageUrl: payment.ad?.imageUrl || "assets/bill2.jpg",
                                                  status: "Paid",
                                                  paidAt: payment.paidAt,
                                                  agentName: toProperCase(agentFullName),
                                                  agentEmail: payment.ad?.agent?.email || "N/A",
                                                  agentPhone: payment.ad?.agent?.phoneNumber || "N/A",
                                                });
                                                setShowPermitModal(true);
                                              }}
                                            >
                                              View Permit
                                            </button>
                                          )}
                                          {(payment.status === "Initialize" ||
                                            payment.status === "AwaitingPayment" ||
                                            payment.status === "UnSuccessful") && (
                                            <button
                                              className="btn btn-sm btn-light-primary btn-active-primary me-2 fs-7"
                                              onClick={() => {
                                                const lat = parseFloat(payment.ad?.location?.latitude);
                                                const lng = parseFloat(payment.ad?.location?.longitude);
                                                setSelectedLocation({
                                                  businessName: toProperCase(getClientName(payment.ad?.client)),
                                                  businessPhone: getClientPhone(payment.ad?.client),
                                                  lga: toProperCase(payment.ad?.location?.lga || ""),
                                                  address: toProperCase(payment.ad?.address || "N/A"),
                                                  lat: !isNaN(lat) ? lat : 6.335,
                                                  lng: !isNaN(lng) ? lng : 5.6037,
                                                  amount: formatCurrency(payment.amount),
                                                  adType: toProperCase(payment.ad?.lookup?.type?.name || ""),
                                                  category: toProperCase(payment.ad?.lookup?.category?.name || ""),
                                                  duration: payment.ad?.duration || "Annual",
                                                  status: payment.status,
                                                  imageUrl: payment.ad?.imageUrl || "assets/bill2.jpg",
                                                });
                                                setShowLocationModal(true);
                                              }}
                                            >
                                              Location
                                            </button>
                                          )}
                                          {payment.status === "UnSuccessful" && (
                                            <button
                                              className="btn btn-sm btn-light-danger btn-active-danger fs-7"
                                              onClick={() => {
                                                setRequeryTarget(payment);
                                                setRequeryResult(null);
                                                setShowRequeryModal(true);
                                              }}
                                            >
                                              Requery
                                            </button>
                                          )}
                                        </td>
                                      </tr>
                                    ))}
                                  </>
                                )}
                              </tbody>
                            </table>
                          </div>
                        </div>
                        {!loading && payments.length > 0 && (
                          <div className="d-flex justify-content-between align-items-center pt-4">
                            <div className="text-muted fs-7">
                              Showing {(currentPage - 1) * ITEMS_PER_PAGE + 1} to{" "}
                              {Math.min(currentPage * ITEMS_PER_PAGE, totalItems)} of {totalItems} records
                            </div>
                            <div>
                              <button
                                className="btn btn-sm btn-light me-2"
                                disabled={currentPage === 1}
                                onClick={() => goToPage(currentPage - 1)}
                              >
                                Previous
                              </button>
                              <span className="fw-bold mx-2 text-gray-600">
                                Page {currentPage} of {totalPages}
                              </span>
                              <button
                                className="btn btn-sm btn-light"
                                disabled={currentPage === totalPages}
                                onClick={() => goToPage(currentPage + 1)}
                              >
                                Next
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="app-footer py-10 mt-10 border-top">
                    <div className="text-center">
                      <span className="text-muted fw-semibold me-1">2026©</span>
                      <a href="https://edsaa.com.ng/" target="_blank" rel="noopener noreferrer" className="text-gray-800 text-hover-primary fw-bold">EDSAA - ADERA</a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {showRequeryModal && requeryTarget && (
          <div className="modal fade show d-block" style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
            <div className="modal-dialog modal-dialog-centered mw-600px">
              <div className="modal-content border-0 shadow-lg rounded-4">
                <div className="modal-header border-0 pb-0">
                  <h5 className="modal-title fw-bold">Payment Requery</h5>
                  <button type="button" className="btn-close" onClick={closeRequeryModal}></button>
                </div>
                <div className="modal-body pt-3">
                  {!requeryResult ? (
                    <>
                      <div className="alert alert-dismissible bg-light-warning d-flex flex-column flex-sm-row p-5 mb-5">
                        <i className="ki-outline ki-shield-cross fs-2hx text-warning me-4 mb-5 mb-sm-0"></i>
                        <div className="d-flex flex-column pe-0 pe-sm-10">
                          <h5 className="mb-1">Confirm Requery</h5>
                          <span>
                            You are about to requery payment for Invoice{" "}
                            <strong>{requeryTarget.code}</strong>.
                          </span>
                        </div>
                      </div>
                      <div className="bg-light p-4 rounded-2 mb-5">
                        <h6 className="fw-bold text-gray-700 mb-3">Payment Details</h6>
                        <div className="row">
                          <div className="col-md-6 mb-2">
                            <span className="text-gray-600">Amount:</span>
                            <div className="fw-bold">{formatCurrency(requeryTarget.amount)}</div>
                          </div>
                          <div className="col-md-6 mb-2">
                            <span className="text-gray-600">Client:</span>
                            <div className="fw-bold">{getClientName(requeryTarget.ad?.client)}</div>
                          </div>
                          <div className="col-md-6 mb-2">
                            <span className="text-gray-600">Date:</span>
                            <div className="fw-bold">{formatDate(requeryTarget.createdAt)}</div>
                          </div>
                        </div>
                      </div>
                    </>
                  ) : (
                    <div
                      className={`alert alert-dismissible ${requeryResult.success ? "bg-light-success" : "bg-light-danger"} d-flex flex-column flex-sm-row p-5 mb-5`}
                    >
                      <i
                        className={`ki-outline ${requeryResult.success ? "ki-check fs-2hx text-success" : "ki-cross-circle fs-2hx text-danger"} me-4 mb-5 mb-sm-0`}
                      ></i>
                      <div className="d-flex flex-column pe-0 pe-sm-10">
                        <h5 className="mb-1">
                          {requeryResult.success ? "Requery Successful!" : "Requery Failed"}
                        </h5>
                        <span>{requeryResult.message}</span>
                        {requeryResult.success && requeryResult.newStatus && (
                          <div className="mt-3">
                            <span className="text-gray-600 me-2">New Status:</span>
                            <span
                              className={`badge ${requeryResult.newStatus === "Successful" ? "badge-light-success" : "badge-light-danger"}`}
                            >
                              {requeryResult.newStatus === "Successful" ? "Paid" : requeryResult.newStatus}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
                <div className="modal-footer border-0">
                  {!requeryResult ? (
                    <div className="d-flex justify-content-between w-100 mt-8">
                      <button
                        className="btn btn-light btn-lg px-5"
                        onClick={closeRequeryModal}
                        disabled={requeryLoading}
                      >
                        Cancel
                      </button>
                      <button
                        className="btn btn-danger btn-lg px-5"
                        onClick={handleRequery}
                        disabled={requeryLoading}
                      >
                        {requeryLoading ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2"></span> Processing...
                          </>
                        ) : (
                          <>
                            <i className="ki-outline ki-shield-cross fs-2 me-2"></i> Confirm Requery
                          </>
                        )}
                      </button>
                    </div>
                  ) : (
                    <div className="d-flex justify-content-end w-100 mt-8">
                      <button className="btn btn-primary btn-lg px-5" onClick={closeRequeryModal}>
                        Close
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        <ReminderModal
          show={showReminderModal}
          onClose={() => setShowReminderModal(false)}
          fetchAllPayments={fetchAllPayments}
          initialTarget={reminderTarget}
        />

        {showPermitModal && selectedPermit && (
          <div className="modal fade show d-block" style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
            <div className="modal-dialog modal-dialog-centered mw-900px">
              <div className="modal-content rounded shadow-lg">
                <div className="modal-header border-0 pb-0 position-relative">
                  <div className="w-100 text-center mb-2">
                    <div className="d-flex justify-content-center align-items-center gap-3 mb-3">
                      <img src="assets/edo.png" alt="Edo Logo" className="h-60px" />
                      <img src="assets/edsal.png" alt="EDSAL Logo" className="h-60px" />
                    </div>
                    <h3 className="fw-bold text-uppercase text-success mb-1">
                      Edo State Signage And Advertisement Agency
                    </h3>
                    <h4 className="fw-bold text-uppercase text-success mb-0">Advertising Permit</h4>
                  </div>
                  <button
                    type="button"
                    className="btn btn-sm btn-icon position-absolute top-0 end-0 m-3"
                    onClick={() => setShowPermitModal(false)}
                  >
                    <i className="ki-outline ki-cross fs-1"></i>
                  </button>
                </div>
                <div className="modal-body py-5 px-lg-10">
                  <div className="d-flex justify-content-center mb-8">
                    <div className="rounded overflow-hidden shadow-sm border" style={{ width: "500px", height: "250px" }}>
                      <img
                        src={selectedPermit.imageUrl}
                        alt="Ad Preview"
                        className="w-100 h-100"
                        style={{ objectFit: "cover" }}
                        onError={(e) => { e.target.src = "assets/bill2.jpg"; }}
                      />
                    </div>
                  </div>
                  <div className="row g-5">
                    <div className="col-md-6">
                      <h6 className="fw-bold text-gray-700 mb-3">Client Information</h6>
                      <div className="bg-light p-4 rounded border">
                        <div className="mb-2"><strong>Name:</strong> {selectedPermit.clientName}</div>
                        <div className="mb-2"><strong>Address:</strong> {selectedPermit.clientAddress}</div>
                        <div className="mb-0"><strong>Contact:</strong> {selectedPermit.clientContact}</div>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <h6 className="fw-bold text-gray-700 mb-3">Permit Details</h6>
                      <div className="bg-light p-4 rounded border">
                        <div className="mb-2"><strong>Ad Amount:</strong> {selectedPermit.amount}</div>
                        <div className="mb-2"><strong>Category:</strong> {selectedPermit.category}</div>
                        <div className="mb-2"><strong>Type:</strong> {selectedPermit.type}</div>
                        <div className="mb-0"><strong>Expiry Date:</strong> {selectedPermit.expiryDate}</div>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <h6 className="fw-bold text-gray-700 mb-3">Advertisement Details</h6>
                      <div className="bg-light p-4 rounded border">
                        <div className="mb-2"><strong>Title:</strong> {selectedPermit.adTitle}</div>
                        <div className="mb-2"><strong>Size:</strong> {selectedPermit.adSize}</div>
                        <div className="mb-2"><strong>Location:</strong> {selectedPermit.adLocation}</div>
                        <div className="mb-0"><strong>Address:</strong> {selectedPermit.adAddress}</div>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <h6 className="fw-bold text-gray-700 mb-3">Payment Details</h6>
                      <div className="bg-light p-4 rounded border">
                        <div className="mb-2">
                          <strong>Status:</strong>{" "}
                          <span className="badge badge-light-success ms-2">{selectedPermit.status}</span>
                        </div>
                        <div className="mb-2">
                          <strong>Paid Date:</strong> {formatDateOnly(selectedPermit.paidAt)}
                        </div>
                        <div className="separator my-2"></div>
                        <div className="mb-1 small text-muted">Agent: {selectedPermit.agentName}</div>
                      </div>
                    </div>
                  </div>
                  <div className="d-flex justify-content-between mt-8">
                    <button
                      className="btn btn-success btn-lg px-5"
                      onClick={() => handleDownloadPermit(selectedPermit.paymentReference)}
                    >
                      <i className="ki-outline ki-file-down fs-2 me-2"></i> Download Permit
                    </button>
                    <button className="btn btn-light btn-lg px-5" onClick={() => setShowPermitModal(false)}>
                      Close
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {showLocationModal && selectedLocation && (
          <div className="modal fade show d-block" style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
            <div className="modal-dialog modal-dialog-centered mw-800px">
              <div className="modal-content rounded-3 border-0">
                <div className="modal-header pb-0 border-0">
                  <h4 className="modal-title fw-bold" style={{ color: "#181C32" }}>
                    Advertisement Location
                  </h4>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setShowLocationModal(false)}
                  ></button>
                </div>
                <div className="modal-body p-5">
                  <div className="d-flex justify-content-center mb-8">
                    <div className="rounded overflow-hidden shadow-sm border" style={{ width: "500px", height: "250px" }}>
                      <img
                        src={selectedLocation.imageUrl}
                        alt="Location Preview"
                        className="w-100 h-100"
                        style={{ objectFit: "cover" }}
                        onError={(e) => { e.target.src = "assets/bill2.jpg"; }}
                      />
                    </div>
                  </div>
                  <div className="row g-5 mb-5">
                    <div className="col-md-6">
                      <h6 className="fw-bold text-gray-700 mb-3">Business Information</h6>
                      <div className="bg-light p-4 rounded-2 h-100">
                        <div className="mb-2">
                          <span className="fw-bold text-gray-800">Name: </span>
                          <span className="text-gray-600">{selectedLocation.businessName}</span>
                        </div>
                        <div className="mb-2">
                          <span className="fw-bold text-gray-800">Address: </span>
                          <span className="text-gray-600">
                            {selectedLocation.address} ({selectedLocation.lga})
                          </span>
                        </div>
                        <div className="mb-0">
                          <span className="fw-bold text-gray-800">Phone: </span>
                          <span className="text-gray-600">{selectedLocation.businessPhone}</span>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <h6 className="fw-bold text-gray-700 mb-3">Ad Details</h6>
                      <div className="bg-light p-4 rounded-2 h-100">
                        <div className="mb-1">
                          <span className="fw-bold">Type:</span> {selectedLocation.adType || "N/A"}
                        </div>
                        <div className="mb-1">
                          <span className="fw-bold">Category:</span> {selectedLocation.category || "N/A"}
                        </div>
                        <div className="mb-1">
                          <span className="fw-bold">Duration:</span> {selectedLocation.duration}
                        </div>
                        <div>
                          <span className="fw-bold">Status:</span>{" "}
                          <span
                            className={`badge ${selectedLocation.status === "Successful" ? "badge-light-success" : "badge-light-warning"}`}
                          >
                            {selectedLocation.status === "Successful"
                              ? "Paid"
                              : selectedLocation.status === "UnSuccessful"
                              ? "Unsuccessful"
                              : "Pending"}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="mb-3">
                    <div
                      className="rounded-2 overflow-hidden"
                      style={{ height: "350px", width: "100%", backgroundColor: "#f5f8fa", position: "relative" }}
                    >
                      {isMapLoaded ? (
                        <GoogleMap
                          mapContainerStyle={{ height: "100%", width: "100%" }}
                          center={{ lat: selectedLocation.lat, lng: selectedLocation.lng }}
                          zoom={15}
                          options={{ disableDefaultUI: false }}
                        >
                          <Marker
                            position={{ lat: selectedLocation.lat, lng: selectedLocation.lng }}
                            label={selectedLocation.businessName}
                          />
                        </GoogleMap>
                      ) : (
                        <div className="d-flex align-items-center justify-content-center h-100">
                          <span className="spinner-border text-primary"></span>
                          <span className="ms-2">Loading Map...</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <div className="modal-footer border-0 pt-0 d-flex justify-content-between">
                  <a
                    href={`https://www.google.com/maps/dir/?api=1&destination=${selectedLocation.lat},${selectedLocation.lng}`}
                    target="_blank"
                    rel="noreferrer"
                    className="btn btn-success btn-lg px-6"
                  >
                    Get Directions
                  </a>
                  <button
                    type="button"
                    className="btn btn-light btn-lg px-6"
                    onClick={() => setShowLocationModal(false)}
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default Payments;